/* istanbul ignore file */
import { GraphQLClient } from 'graphql-request';
import { Env } from '@stencil/core';
import { extractPid, parseEncodedPid } from '@utils/userUtils';
import { getSdk } from '@lib/graphql-types';

type GraphQLClientOptions = {
  authToken?: string;
  url?: string;
  pid?: string;
  cookieValue?: string;
  signal?: AbortSignal | null;
  cache?: RequestCache;
};

export const createGraphQLClient = ({
  authToken = process.env.HELIOS_AUTH_TOKEN,
  url = process.env.HELIOS_API_URL,
  pid,
  cookieValue,
  signal,
  cache = 'no-cache',
}: GraphQLClientOptions) => {
  if (!Boolean(url)) {
    console.error('GraphQLClient: URL is not defined');
    return null;
  }
  return new GraphQLClient(url, {
    errorPolicy: 'all',
    cache,
    headers: {
      'helios-client-name': process.env.HELIOS_CLIENT_NAME,
      // TODO: Uncomment this line when the backend is ready
      // 'x-authex-client-id': process.env.HELIOS_CLIENT_NAME,
      ...(authToken && { Authorization: `Bearer ${authToken}` }), // Include only if provided
      ...(pid && { pid }), // Include only if provided
      ...(cookieValue && { Cookie: cookieValue }), // Include only if provided
    },
    credentials: 'omit', // Ensures cookies are not sent with requests to avoid CORS issues
    signal, // signal is used to abort the request if the component is unmounted
  });
};

export const sportslineGraphQLClient = () => {
  try {
    const pid = extractPid();
    const encryptedPid = Boolean(pid) ? parseEncodedPid(pid) : null;
    const abortController = new AbortController();
    const client = createGraphQLClient({
      signal: abortController.signal,
      authToken: Env.HELIOS_AUTH_TOKEN,
      url: Env.HELIOS_API_URL,
      ...(pid && { pid }),
    });

    if (!Boolean(client)) {
      return null;
    }
    const sdk = getSdk(client);
    return {
      dataFetcher: sdk,
      pid,
      encryptedPid,
    };
  } catch (e) {
    console.error('Error creating GraphQL client:', e);
    return null;
  }
};
